/**
 * MIS — manažerský přehled (§3 snímky 23–25, §4.6).
 *
 * The page answers four management questions and is explicit about the ones it
 * cannot answer: a plant without an irradiance sensor has no performance ratio,
 * a plant without a nameplate capacity is not in the ranking, and a month
 * without data is a gap rather than a zero.
 */

import type { TFunction } from 'i18next';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { MisPeriod } from '../api/types';
import { Chart, type ChartPanel } from '../components/Chart';
import { KpiCard } from '../components/KpiCard';
import { Notice } from '../components/Notice';
import {
  formatCapacity,
  formatDateTime,
  formatEnergy,
  formatNumber,
  formatPercent,
  formatPower,
} from '../lib/format';
import { useApi } from '../lib/useApi';

const PERIODS: MisPeriod[] = [
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
  'last_12_months',
];

function formatDuration(seconds: number | null | undefined, t: TFunction) {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 3600) return t('mis.minutes', { value: Math.round(seconds / 60) });
  if (seconds < 86400) return t('mis.hours', { value: (seconds / 3600).toFixed(1) });
  return t('mis.days', { value: (seconds / 86400).toFixed(1) });
}

export function MisPage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<MisPeriod>('this_month');
  const [showTrendTable, setShowTrendTable] = useState(false);
  const { data, loading, errorCode, errorParams } = useApi(
    () => api.misSummary({ period, trendMonths: 12 }),
    [period],
  );

  const trendPanels: ChartPanel[] = data
    ? [
        {
          axisLabel: t('mis.energyKwh'),
          categories: data.monthly_trend.map((point) => point.month),
          series: [
            {
              name: t('mis.monthlyEnergy'),
              colorIndex: 0,
              kind: 'bar',
              points: data.monthly_trend.map((point) =>
                point.energy_wh === null ? null : point.energy_wh / 1000,
              ),
            },
          ],
        },
      ]
    : [];

  return (
    <>
      <h1>{t('nav.mis')}</h1>

      <div className="toolbar">
        <div className="segmented" role="group" aria-label={t('chart.range')}>
          {PERIODS.map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={period === key}
              className={period === key ? 'is-active' : ''}
              onClick={() => setPeriod(key)}
            >
              {t(`mis.periods.${key}`)}
            </button>
          ))}
        </div>
        {data ? (
          <>
            <span className="muted">
              {formatDateTime(data.period.from)} – {formatDateTime(data.period.to)}
            </span>
            <a className="button button--ghost" href={api.misCsvUrl({ period, trendMonths: 12 })}>
              {t('report.downloadCsv')}
            </a>
          </>
        ) : null}
      </div>

      <Notice tone="error" code={errorCode} params={errorParams} />
      {loading && !data ? <p className="muted">{t('app.loading')}</p> : null}

      {data ? (
        <>
          <div className="kpi-grid">
            <KpiCard
              label={t('mis.energy')}
              value={formatEnergy(data.totals.energy_wh)}
              hint={`${t('mis.plantCount', { count: data.totals.plant_count })} · ${formatCapacity(
                data.totals.dc_capacity_wp,
              )}`}
            />
            <KpiCard
              label={t('kpi.specificYield')}
              value={
                data.totals.specific_yield_wh_per_wp === null
                  ? '—'
                  : `${formatNumber(data.totals.specific_yield_wh_per_wp, 1)} Wh/Wp`
              }
              hint={
                data.totals.plants_without_capacity > 0
                  ? t('mis.capacityMissing', { count: data.totals.plants_without_capacity })
                  : null
              }
              tone={data.totals.plants_without_capacity > 0 ? 'warning' : 'default'}
            />
            <KpiCard label={t('mis.peakPower')} value={formatPower(data.totals.peak_power_w)} />
            <KpiCard
              label={t('mis.dataAvailability')}
              value={formatPercent(data.totals.data_availability, 1)}
              hint={t('mis.availabilityHint')}
              tone={
                data.totals.data_availability !== null && data.totals.data_availability < 0.95
                  ? 'warning'
                  : 'default'
              }
            />
            <KpiCard
              label={t('mis.incidentsOpened')}
              value={formatNumber(data.incidents.opened, 0)}
              hint={`${t('mis.closed')}: ${data.incidents.closed} · ${t('kpi.openIncidents')}: ${
                data.incidents.open_now
              }`}
              tone={data.incidents.open_now > 0 ? 'critical' : 'default'}
            />
            <KpiCard
              label={t('mis.mttr')}
              value={formatDuration(data.incidents.mttr_seconds, t)}
              hint={`${t('mis.mtta')}: ${formatDuration(data.incidents.mtta_seconds, t)}`}
            />
          </div>

          {data.notes.map((note) => (
            <Notice key={note} tone={note === 'availability_is_data_based' ? 'info' : 'warning'}>
              {t(`mis.notes.${note}`, { defaultValue: note })}
            </Notice>
          ))}

          <section>
            <h2>{t('mis.trend')}</h2>
            <figure className="series">
              <figcaption className="series__head">
                <h3>{t('mis.monthlyEnergy')}</h3>
                <div className="series__actions">
                  <button type="button" onClick={() => setShowTrendTable((value) => !value)}>
                    {showTrendTable ? t('app.chart') : t('app.table')}
                  </button>
                </div>
              </figcaption>
              {showTrendTable ? (
                <div className="table-wrap">
                  <table>
                    <caption className="sr-only">{t('mis.monthlyEnergy')}</caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('mis.month')}</th>
                        <th scope="col" className="num">{t('mis.energy')}</th>
                        <th scope="col" className="num">{t('kpi.specificYield')}</th>
                        <th scope="col" className="num">{t('mis.dataAvailability')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.monthly_trend.map((point) => (
                        <tr key={point.month}>
                          <th scope="row">{point.month}</th>
                          <td className="num">
                            {point.energy_wh === null ? t('mis.noData') : formatEnergy(point.energy_wh)}
                          </td>
                          <td className="num">
                            {point.specific_yield_wh_per_wp === null
                              ? '—'
                              : `${formatNumber(point.specific_yield_wh_per_wp, 1)} Wh/Wp`}
                          </td>
                          <td className="num">{formatPercent(point.data_availability, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Chart panels={trendPanels} ariaLabel={t('mis.monthlyEnergy')} height={280} />
              )}
            </figure>
            {data.monthly_trend.some((point) => point.energy_wh === null) ? (
              <Notice tone="info">{t('mis.notes.months_without_data')}</Notice>
            ) : null}
          </section>

          <section>
            <h2>{t('mis.ranking')}</h2>
            {data.ranking.excluded_plants > 0 ? (
              <Notice tone="warning">
                {t('mis.rankingExcluded', { count: data.ranking.excluded_plants })}
              </Notice>
            ) : null}
            <div className="table-wrap">
              <table>
                <caption className="sr-only">{t('mis.ranking')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('nav.plants')}</th>
                    <th scope="col" className="num">{t('mis.energy')}</th>
                    <th scope="col" className="num">{t('kpi.specificYield')}</th>
                    <th scope="col" className="num">{t('mis.performanceRatio')}</th>
                    <th scope="col" className="num">{t('mis.peakPower')}</th>
                    <th scope="col" className="num">{t('mis.dataAvailability')}</th>
                    <th scope="col" className="num">{t('kpi.devices')}</th>
                    <th scope="col" className="num">{t('mis.incidentsOpened')}</th>
                    <th scope="col" className="num">{t('mis.mttr')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plants.map((plant) => (
                    <tr key={plant.plant_id}>
                      <th scope="row">
                        <Link to={`/plants/${plant.plant_id}`}>{plant.name}</Link>
                        <span className="muted"> · {plant.code}</span>
                      </th>
                      <td className="num">{formatEnergy(plant.energy_wh)}</td>
                      <td className="num">
                        {plant.specific_yield_wh_per_wp === null
                          ? '—'
                          : `${formatNumber(plant.specific_yield_wh_per_wp, 1)} Wh/Wp`}
                      </td>
                      <td className="num">
                        {plant.performance_ratio === null ? (
                          <span
                            className="muted"
                            title={
                              plant.has_irradiance_sensor
                                ? t('mis.notes.irradiance_coverage_low')
                                : t('mis.notes.no_irradiance_sensor')
                            }
                          >
                            {t('mis.notAvailable')}
                          </span>
                        ) : (
                          formatPercent(plant.performance_ratio, 1)
                        )}
                      </td>
                      <td className="num">{formatPower(plant.peak_power_w)}</td>
                      <td className="num">{formatPercent(plant.data_availability, 0)}</td>
                      <td className="num">
                        {plant.devices_reporting}/{plant.device_count}
                      </td>
                      <td className="num">{plant.incidents.opened}</td>
                      <td className="num">{formatDuration(plant.incidents.mttr_seconds, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2>{t('mis.service')}</h2>
            <div className="table-wrap">
              <table>
                <caption className="sr-only">{t('mis.service')}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('incidents.severity')}</th>
                    <th scope="col" className="num">{t('mis.incidentsOpened')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.incidents.by_severity).map(([severity, count]) => (
                    <tr key={severity}>
                      <th scope="row">{t(`incidents.severities.${severity}`)}</th>
                      <td className="num">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {Object.keys(data.incidents.by_rule).length > 0 ? (
              <>
                <h3 className="subhead">{t('mis.byRule')}</h3>
                <div className="table-wrap">
                  <table>
                    <caption className="sr-only">{t('mis.byRule')}</caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('mis.rule')}</th>
                        <th scope="col" className="num">{t('mis.incidentsOpened')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(data.incidents.by_rule).map(([rule, count]) => (
                        <tr key={rule}>
                          <th scope="row">
                            {t(`mis.ruleNames.${rule}`, { defaultValue: rule })}
                          </th>
                          <td className="num">{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </section>

          <p className="muted">
            {t('mis.generatedAt', { value: formatDateTime(data.generated_at) })}
          </p>
        </>
      ) : null}
    </>
  );
}
